/**
 * Downloadable content packs v2 (curriculum 2, docs/PERF.md "Content packs v2"): a release is a DELTA of per-world
 * chunks (+ optionally a new shell and locale catalogs) over the previous release / the bundled build. The client asks
 * for the manifest (chunk hashes), downloads only the parts whose hash differs from what it has, checks each part
 * here and swaps it in. The server checks the whole effective pack with the full validator before it publishes.
 */
import type { Locale } from '../i18n/locales';
import { checkArtDrawing, PATH_DATA } from './artCheck';
import { DERIVED_ITEM_TYPES, MINI_UNIT_ITEM_TYPES, V2_ITEM_TYPES, type Drawing, type MiniUnitItemType } from './model';
import { SHELL_SCHEMA, contentHash, type ChunkId, type ContentShell, type WorldChunk } from './split';

/** Envelope schema of v2 releases (v1 whole-pack releases use CONTENT_SCHEMA = 2). */
export const PACK_SCHEMA_V2 = 3;

/** Catalog of one locale for the shell and each chunk (English key → translation). */
export interface PackLocales {
  shell?: Record<string, string>;
  chunks?: Partial<Record<ChunkId, Record<string, string>>>;
}

/** What the publisher sends: only what changed. */
export interface ContentReleaseV2 {
  schema: typeof PACK_SCHEMA_V2;
  curriculum: 2;
  version: number;
  shell?: ContentShell;
  chunks?: WorldChunk[];
  locales?: Partial<Record<Locale, PackLocales>>;
}

/** What the client reads first (small): hashes of every part of the effective content. */
export interface ContentManifestV2 {
  schema: typeof PACK_SCHEMA_V2;
  curriculum: 2;
  version: number;
  shell: string;
  chunks: Partial<Record<ChunkId, string>>;
  /** locale → part ('shell' | chunk id) → hash */
  locales: Partial<Record<Locale, Record<string, string>>>;
}

export const catalogHash = (c: Record<string, string> | undefined) => (c ? contentHash(c) : '');
export const shellHash = (s: ContentShell) => contentHash(s);

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const isL = (x: unknown) => isObj(x) && typeof x.en === 'string';

/** Structural check of a shell (client side, before it replaces the live one). */
export function checkShell(x: unknown): string[] {
  const e: string[] = [];
  if (!isObj(x)) return ['shell: not an object'];
  const s = x as unknown as ContentShell;
  if (s.schema !== SHELL_SCHEMA) e.push(`shell: schema ${String(s.schema)}`);
  if (s.curriculum !== 2) e.push('shell: not curriculum 2');
  if (!isObj(s.pack) || !Array.isArray(s.pack.drawings) || !Array.isArray(s.pack.miniUnits) || !Array.isArray(s.pack.paths) || !isObj(s.pack.playground)) return [...e, 'shell: malformed pack'];
  if (!isObj(s.drawingChunk) || !isObj(s.deps) || !isObj(s.chunks)) e.push('shell: missing lookups');
  const ids = new Set<string>();
  for (const d of s.pack.drawings) {
    if (!d || typeof d.id !== 'string' || !isL(d.title)) e.push(`shell: bad drawing ${String(d?.id)}`);
    else if (ids.has(d.id)) e.push(`shell: duplicate drawing ${d.id}`);
    else ids.add(d.id);
    if (d && !s.drawingChunk?.[d.id]) e.push(`shell: drawing ${d.id} has no chunk`);
  }
  const mus = new Set<string>();
  for (const m of s.pack.miniUnits) {
    if (!m || typeof m.id !== 'string' || mus.has(m.id)) e.push(`shell: bad mini unit ${String(m?.id)}`);
    else mus.add(m.id);
    if (m?.drawingId && !ids.has(m.drawingId)) e.push(`shell: ${m.id} → unknown drawing ${m.drawingId}`);
    if (m && !Array.isArray(s.deps?.[m.id])) e.push(`shell: ${m?.id} has no deps`);
  }
  for (const p of s.pack.paths) for (const u of p.units) for (const i of u.items) if (i.miniUnitId && !mus.has(i.miniUnitId)) e.push(`shell: path ${p.id} → unknown mini unit ${i.miniUnitId}`);
  for (const i of s.pack.playground.items ?? []) if (!ids.has(i.drawingId)) e.push(`shell: playground ${i.id} → unknown drawing ${i.drawingId}`);
  return e.slice(0, 40);
}

/**
 * Check one chunk against the shell it will hydrate (client side, before the swap): ids known, hash right, every
 * drawing has steps with valid path data and a consistent figure, every mini-unit item has a known type and points at
 * known drawings.
 */
export function checkWorldChunk(x: unknown, shell: Pick<ContentShell, 'drawingChunk' | 'pack'>, expect?: { chunk?: ChunkId; hash?: string }): string[] {
  const e: string[] = [];
  if (!isObj(x)) return ['chunk: not an object'];
  const c = x as unknown as WorldChunk;
  const tag = `chunk ${String(c.chunk)}`;
  if (c.schema !== SHELL_SCHEMA) e.push(`${tag}: schema ${String(c.schema)}`);
  if (!isObj(c.drawings) || !isObj(c.miniUnits)) return [...e, `${tag}: malformed`];
  if (expect?.chunk && c.chunk !== expect.chunk) e.push(`${tag}: expected ${expect.chunk}`);
  const real = contentHash({ d: c.drawings, m: c.miniUnits, s: c.story ?? null });
  if (c.hash !== real) e.push(`${tag}: hash mismatch`);
  if (expect?.hash && c.hash !== expect.hash) e.push(`${tag}: not the announced version`);
  const light = new Map(shell.pack.drawings.map((d) => [d.id, d] as const));
  const types = new Set<MiniUnitItemType>([...MINI_UNIT_ITEM_TYPES, ...V2_ITEM_TYPES, ...DERIVED_ITEM_TYPES]);
  for (const [id, h] of Object.entries(c.drawings)) {
    const d = light.get(id);
    if (!d) {
      e.push(`${tag}: unknown drawing ${id}`);
      continue;
    }
    if (shell.drawingChunk[id] !== c.chunk) e.push(`${tag}: drawing ${id} belongs to ${shell.drawingChunk[id]}`);
    if (!Array.isArray(h.steps) || !h.steps.length) {
      e.push(`${id}: no steps`);
      continue;
    }
    h.steps.forEach((st, i) => {
      if (!isL(st?.caption)) e.push(`${id} step ${i + 1}: no caption`);
      if (!Array.isArray(st?.strokes)) return void e.push(`${id} step ${i + 1}: no strokes`);
      for (const s of st.strokes) if (typeof s?.d !== 'string' || !PATH_DATA.test(s.d)) e.push(`${id} step ${i + 1}: invalid path data`);
    });
    if (h.art) for (const m of checkArtDrawing({ ...(d as Drawing), steps: h.steps, art: h.art })) e.push(`${id}: ${m}`);
  }
  const mus = new Set(shell.pack.miniUnits.map((m) => m.id));
  for (const [id, items] of Object.entries(c.miniUnits)) {
    if (!mus.has(id)) e.push(`${tag}: unknown mini unit ${id}`);
    if (!Array.isArray(items) || !items.length) {
      e.push(`${id}: no items`);
      continue;
    }
    for (const it of items) {
      if (!it || typeof it.id !== 'string' || !types.has(it.type)) e.push(`${id}: bad item ${String(it?.id)} (${String(it?.type)})`);
      const did = (it as { drawingId?: string }).drawingId;
      if (did && !light.has(did)) e.push(`${id}: item ${it.id} → unknown drawing ${did}`);
    }
  }
  if (c.story && (c.story.world !== c.chunk || !Array.isArray(c.story.cast))) e.push(`${tag}: bad story layer`);
  return e.slice(0, 40);
}

/** A catalog is a flat string → string map. */
export const checkCatalog = (x: unknown): boolean => isObj(x) && Object.values(x).every((v) => typeof v === 'string');

/** Overlay a delta release on the current effective state (server: compose before validating / serving). */
export function overlayRelease(
  base: { shell: ContentShell; chunks: Map<ChunkId, WorldChunk>; locales: Map<string, Record<string, string>> },
  rel: ContentReleaseV2,
): { shell: ContentShell; chunks: Map<ChunkId, WorldChunk>; locales: Map<string, Record<string, string>> } {
  const chunks = new Map(base.chunks);
  for (const c of rel.chunks ?? []) chunks.set(c.chunk, c);
  const shell: ContentShell = rel.shell ? { ...rel.shell } : { ...base.shell };
  shell.version = rel.version;
  shell.pack = { ...shell.pack, version: rel.version };
  shell.chunks = Object.fromEntries([...chunks.values()].map((c) => [c.chunk, c.hash]));
  const locales = new Map(base.locales);
  for (const [loc, l] of Object.entries(rel.locales ?? {})) {
    if (!l) continue;
    if (l.shell) locales.set(`${loc}|shell`, l.shell);
    for (const [cid, cat] of Object.entries(l.chunks ?? {})) if (cat) locales.set(`${loc}|${cid}`, cat);
  }
  return { shell, chunks, locales };
}

export function manifestOf(state: { shell: ContentShell; chunks: Map<ChunkId, WorldChunk>; locales: Map<string, Record<string, string>> }): ContentManifestV2 {
  const locales: ContentManifestV2['locales'] = {};
  for (const [k, cat] of state.locales) {
    const [loc, part] = k.split('|') as [Locale, string];
    (locales[loc] ??= {})[part] = catalogHash(cat);
  }
  return {
    schema: PACK_SCHEMA_V2,
    curriculum: 2,
    version: state.shell.version,
    shell: shellHash(state.shell),
    chunks: Object.fromEntries([...state.chunks.values()].map((c) => [c.chunk, c.hash])),
    locales,
  };
}

/** Rebuild a full pack from a shell + every chunk (server-side validation of a release). Deep-copies the shell. */
export function assemblePack(shell: ContentShell, chunks: Iterable<WorldChunk>) {
  const pack = JSON.parse(JSON.stringify(shell.pack)) as ContentShell['pack'];
  const byId = new Map(pack.drawings.map((d) => [d.id, d] as const));
  const muById = new Map(pack.miniUnits.map((m) => [m.id, m] as const));
  const stories = [];
  for (const c of chunks) {
    for (const [id, h] of Object.entries(c.drawings)) {
      const d = byId.get(id) as (Drawing & { $light?: true; $n?: number; $sig?: string }) | undefined;
      if (!d) continue;
      d.steps = h.steps;
      if (h.art) d.art = h.art;
      if (h.remix) d.remix = h.remix;
      delete d.$light;
      delete d.$n;
      delete d.$sig;
    }
    for (const [id, items] of Object.entries(c.miniUnits)) {
      const m = muById.get(id) as (typeof pack.miniUnits)[number] & { $n?: number };
      if (!m) continue;
      m.items = items;
      delete m.$n;
    }
    if (c.story) stories.push(c.story);
  }
  return { pack, stories };
}
